import api from "./client";
import { downloadAuthenticatedFile } from "./pdf";

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");
export const getDashboardWidgets = () => api.get("/dashboard/widgets");
export const getSidebarCounts = () => api.get("/sidebar/counts");

// Guests (reusable guest profiles)
export const searchGuests = (query) => api.get("/guests/search", { params: { q: query } });
export const saveGuest = (data) => api.post("/guests", data);
export const getGuestDirectory = (params) => api.get("/guests", { params });
export const getGuestSuggestions = (bookingId) => api.get(`/guests/suggest/${encodeURIComponent(bookingId)}`);

// Hygiene
export const getHygieneReport = () => api.get("/hygiene/report");
// No /hygiene/run-cleanup route exists in the API, so the export and the
// button that called it were removed on 2026-08-27.

// Seed data
export const seedDemoData = () => api.post("/seed-demo-data");

// Health check
export const healthCheck = () => api.get("/health");

// Admin - Classifieds Review Queue
export const getPendingClassifieds = (params) => api.get("/admin/classifieds/pending-review", { params });

// Admin - API Keys
export const getApiKeys = () => api.get("/admin/api-keys");
export const createApiKey = (data) => api.post("/admin/api-keys", data);
export const revokeApiKey = (appName) => api.delete(`/admin/api-keys/${encodeURIComponent(appName)}`);
export const activateApiKey = (appName) => api.post(`/admin/api-keys/${encodeURIComponent(appName)}/activate`);

// Tickets
export const getTickets = (params) => api.get("/tickets", { params });
export const getTicket = (ticketId) => api.get(`/tickets/${ticketId}`);
export const createTicket = (data) => api.post("/tickets", data);
export const updateTicket = (ticketId, data) => api.patch(`/tickets/${ticketId}`, data);
export const addTicketComment = (ticketId, data) => api.post(`/tickets/${ticketId}/comment`, data);
export const getTicketsDashboard = () => api.get("/tickets/dashboard-summary");
export const startWorkingTicket = (ticketId) => api.post(`/tickets/${ticketId}/start-working`);
export const resolveTicket = (ticketId) => api.post(`/tickets/${ticketId}/resolve`);
export const closeTicket = (ticketId) => api.post(`/tickets/${ticketId}/close`);
export const reopenTicket = (ticketId) => api.post(`/tickets/${ticketId}/reopen`);

// Viewings
export const createViewing = (data) => api.post("/viewings", data);
export const getViewings = (params) => api.get("/viewings", { params });
export const getViewing = (viewingId) => api.get(`/viewings/${viewingId}`);
export const updateViewing = (viewingId, data) => api.patch(`/viewings/${viewingId}`, data);
export const createViewingFromExtraction = (data) => api.post("/viewings/from-extraction", data);
export const sendViewerRequest = (viewingId) => api.post(`/viewings/${viewingId}/send-viewer-request`);
export const sendViewingAuthForms = (viewingId, data) => api.post(`/viewings/${viewingId}/send-auth-forms`, data || {});
export const shareViewingContacts = (viewingId) => api.post(`/viewings/${viewingId}/share-contacts`);
export const dismissViewingRequest = (viewingId) => api.post(`/viewings/${viewingId}/dismiss`);
export const sendTimeConfirmation = (viewingId) => api.post(`/viewings/${viewingId}/send-time-confirmation`);

// Viewing Sessions (multi-property)
export const createViewingSession = (data) => api.post("/viewing-sessions", data);
export const getViewingSessions = (params) => api.get("/viewing-sessions", { params });
export const getViewingSession = (sessionId) => api.get(`/viewing-sessions/${sessionId}`);
export const sendSessionViewerAuth = (sessionId) => api.post(`/viewing-sessions/${sessionId}/send-viewer-auth`);
export const sendSessionSupplierAuth = (sessionId, supplierId) => api.post(`/viewing-sessions/${sessionId}/send-supplier-auth/${supplierId}`);
export const cancelViewingSession = (sessionId) => api.post(`/viewing-sessions/${sessionId}/cancel`);

// WhatsApp Approvals
export const getWhatsAppApprovals = (params) => api.get("/whatsapp-approvals", { params });
export const approveWhatsAppMessage = (approvalId) => api.post(`/whatsapp-approvals/${approvalId}/approve`);
export const skipWhatsAppMessage = (approvalId) => api.post(`/whatsapp-approvals/${approvalId}/skip`);

// QA Inspector
export const getQARuns = (params) => api.get("/qa/runs", { params });
export const getQARun = (runId) => api.get(`/qa/runs/${runId}`);
export const triggerQASmoke = () => api.post("/qa/trigger/smoke");
export const triggerQAFull = () => api.post("/qa/trigger/full");
export const getQAProposals = (params) => api.get("/qa/proposals", { params });
export const getQAProposal = (id) => api.get(`/qa/proposals/${id}`);
export const approveProposal = (id) => api.post(`/qa/proposals/${id}/approve`);
export const rejectProposal = (id, reason) => api.post(`/qa/proposals/${id}/reject`, null, { params: { reason } });
export const getQATrends = (days = 30) => api.get("/qa/trends", { params: { days } });
export const getQAStatus = () => api.get("/qa/status");

// API Costs (Apify + LLM + Google Maps + Twilio)
export const getApiCosts = (days = 7) => api.get("/monitoring/api-costs", { params: { days } });
export const getLlmCosts = (days = 7) => api.get("/monitoring/llm-costs", { params: { days } });

// Scheduler Health
export const getSchedulerHealth = () => api.get("/admin/scheduler-health");
export const getSchedulerRuns = (scheduler, limit = 20) =>
  api.get("/admin/scheduler-runs", { params: { scheduler, limit } });
export const getSchedulerRunDetail = (schedulerName, runId) =>
  api.get(`/admin/scheduler-runs/${encodeURIComponent(schedulerName)}/${encodeURIComponent(runId)}`);

// Paragon manual triggers
export const triggerParagonRun = (action, dryRun = true) =>
  api.post("/admin/paragon/run-now", null, { params: { action, dry_run: dryRun }, timeout: 120000 });
export const getParagonHeartbeat = () => api.get("/admin/paragon/heartbeat");
export const verifyPlaywright = () => api.post("/admin/paragon/verify-playwright");

// Invoice payment state override
export const overrideInvoicePaymentState = (invoiceNumber, data) =>
  api.post(`/admin/invoices/${encodeURIComponent(invoiceNumber)}/payment-state-override`, data);
export const checkPaymentInvariants = () => api.get("/admin/invoices/payment-invariant-check");

// System Health
export const getSystemHealth = () => api.get("/admin/system-health");
export const getCheckHistory = (checkName, limit = 12) =>
  api.get(`/admin/system-health/${encodeURIComponent(checkName)}/history`, { params: { limit } });
export const triggerHealthChecks = (category) =>
  api.post("/admin/system-health/run", null, { params: category ? { category } : {} });

// Xero sync reconciliation mode
export const getXeroReconciliationMode = () => api.get("/admin/xero-sync/reconciliation-mode");
export const setXeroReconciliationMode = (mode) =>
  api.post("/admin/xero-sync/reconciliation-mode", { mode });

// Payment violation review
export const getPaymentViolations = () => api.get("/admin/payment-violations");
export const reviewPaymentViolation = (invoiceId, data) =>
  api.post(`/admin/payment-violations/${encodeURIComponent(invoiceId)}/review`, data);
export const flipToEnforce = () =>
  api.post("/admin/payment-violations/flip-to-enforce", {});
export const getViolationReviewHistory = (invoiceId) =>
  api.get(`/admin/payment-violations/${encodeURIComponent(invoiceId)}/history`);

// Inbox fetch health
export const getInboxHealth = () => api.get("/admin/inbox-health");

// Audit trail (lifecycle shadow mode)
export const getAuditTrail = (params) => api.get("/admin/audit-trail", { params });

// Shadow disagreements
export const getShadowDisagreements = (params) => api.get("/admin/shadow-disagreements", { params });

// Lifecycle approvals (gated transitions)
export const getLifecycleApprovals = (params) => api.get("/admin/approvals", { params });
export const getLifecycleApproval = (proposalId) => api.get(`/admin/approvals/${proposalId}`);
export const approveLifecycleProposal = (proposalId, data) =>
  api.post(`/admin/approvals/${proposalId}/approve`, data);
export const rejectLifecycleProposal = (proposalId, data) =>
  api.post(`/admin/approvals/${proposalId}/reject`, data);

// Lifecycle observability (parameterised: enquiry | search | option)
export const getLifecycleObservability = (lifecycle = "enquiry") =>
  api.get("/admin/lifecycle-observability", { params: { lifecycle } });

// Historical disagreement audit
export const getHistoricalDisagreements = (params) => api.get("/admin/lifecycle/historical-disagreements", { params });

// Migrations
export const runMigration = (migrationName, dryRun = true) =>
  api.post(`/admin/migrations/${encodeURIComponent(migrationName)}`, { dry_run: dryRun }, { timeout: 120000 });

// Diagnostics
export const getChaseLadderGates = () => api.get("/admin/diagnostics/chase-ladder-gates");
export const getOutreachHealth = () => api.get("/admin/diagnostics/outreach-health");
export const getProcessMemory = () => api.get("/admin/diagnostics/process-memory");

// Voice follow-up diagnostics
export const getVoiceFollowups = (params) =>
  api.get("/admin/diagnostics/voice-followups", { params });
export const getVoiceChasingToggle = () =>
  api.get("/admin/diagnostics/voice-chasing-toggle");
export const setVoiceChasingToggle = (enabled) =>
  api.post("/admin/diagnostics/voice-chasing-toggle", { enabled });

// Location normalization preview
export const getLocationNormalizationPreview = (raw, countryHint) =>
  api.get("/admin/diagnostics/location-normalization-preview", {
    params: { raw, ...(countryHint ? { country_hint: countryHint } : {}) },
  });

// Driving time preview
export const getDrivingTimePreview = (originLat, originLng, destLat, destLng) =>
  api.get("/admin/diagnostics/driving-time-preview", {
    params: { origin_lat: originLat, origin_lng: originLng, destination_lat: destLat, destination_lng: destLng },
  });

// Task heartbeats (background task staleness monitoring)
export const getTaskHeartbeats = () => api.get("/admin/task-heartbeats");
export const getTaskRecoveryEvents = (taskId, limit = 50) =>
  api.get("/admin/task-recovery-events", { params: { task_id: taskId, limit } });
export const resetTaskHeartbeat = (taskId, dryRun = true) =>
  api.post(`/admin/task/${encodeURIComponent(taskId)}/reset`, null, { params: { dry_run: dryRun } });

// Xero tax rates status
export const getXeroTaxRatesStatus = () => api.get("/admin/xero/tax-rates-status");

// Revenue reconciliation diagnostic
export const getRevenueReconciliation = (months = 12) =>
  api.get("/admin/financials/revenue-reconciliation", { params: { months } });

// Lifecycle drift reconciliation
export const getLifecycleDrift = () => api.get("/admin/lifecycle/drift");
export const getDriftRepairStatus = () => api.get("/admin/lifecycle/drift-repair-status");
export const reconcileLifecycleDrift = (data) =>
  api.post("/admin/lifecycle/reconcile", data, { timeout: 120000 });
export const reconcileLifecycleDriftBulk = (data) =>
  api.post("/admin/lifecycle/reconcile-bulk", data, { timeout: 120000 });

// Status-lifecycle mismatch diagnostic & repair
export const getStatusLifecycleMismatch = () => api.get("/admin/diagnostics/status-lifecycle-mismatch");
export const repairStatusLifecycleMismatch = (data) =>
  api.post("/admin/diagnostics/status-lifecycle-mismatch/repair", data);

// Status-lifecycle drift (general) diagnostic & repair
export const getStatusLifecycleDrift = () => api.get("/admin/diagnostics/status-lifecycle-drift");
export const repairStatusLifecycleDrift = (data) =>
  api.post("/admin/diagnostics/status-lifecycle-drift/repair", data);

// Budget basis backfill diagnostic & repair
export const getBudgetBasisBackfill = () => api.get("/admin/diagnostics/budget-basis-backfill");
export const repairBudgetBasisBackfill = (data) =>
  api.post("/admin/diagnostics/budget-basis-backfill/repair", data);

// Budget amount backfill diagnostic & repair
export const getBudgetAmountBackfill = () => api.get("/admin/diagnostics/budget-amount-backfill");
export const repairBudgetAmountBackfill = (data) =>
  api.post("/admin/diagnostics/budget-amount-backfill/repair", data);

// Preferences null repair diagnostic & repair
export const getPreferencesNullClients = () => api.get("/admin/diagnostics/preferences-null-clients");
export const repairPreferencesNullClients = (data) =>
  api.post("/admin/diagnostics/preferences-null-clients/repair", data);

// Budget basis propagation diagnostic (read-only)
export const getBudgetBasisPropagation = () =>
  api.get("/admin/diagnostics/budget-basis-propagation");

// Wrong-basis residue diagnostic (ENQ-00193-class, read-only)
export const getWrongBasisResidue = () =>
  api.get("/admin/diagnostics/wrong-basis-residue");

// Client budget basis declaration
export const getClientBudgetBasis = () => api.get("/admin/client-budget-basis");
export const patchClientBudgetBasis = (clientId, data) =>
  api.patch(`/admin/client-budget-basis/${encodeURIComponent(clientId)}`, data);

// Manual payment matching
export const getUnmatchedPayments = () => api.get("/admin/payments/unmatched");
export const searchInvoicesForMatching = (params) =>
  api.get("/admin/payments/search-invoices", { params });
export const manualMatchPayment = (paymentId, data) =>
  api.post(`/admin/payments/${encodeURIComponent(paymentId)}/manual-match`, data);
export const unmatchPayment = (paymentId) =>
  api.post(`/admin/payments/${encodeURIComponent(paymentId)}/unmatch`);

// WOP supplier_id backfill
export const getWopSupplierIdBackfillPreview = () =>
  api.get("/admin/wop-backfill/supplier-id/preview");
export const executeWopSupplierIdBackfill = () =>
  api.post("/admin/wop-backfill/supplier-id/execute");

// Payment-match provenance backfill
export const getPaymentMatchBackfillPreview = () =>
  api.get("/admin/payment-match-backfill");
export const executePaymentMatchBackfill = () =>
  api.post("/admin/payment-match-backfill", { confirm: true });

// Payment-match allocation repair (multi-invoice allocated_amount fix)
export const getPaymentMatchAllocationRepairPreview = () =>
  api.get("/admin/payment-match-allocation-repair");
export const executePaymentMatchAllocationRepair = () =>
  api.post("/admin/payment-match-allocation-repair", { confirm: true });

// Quote duplicate cluster diagnostic (read-only)
export const getQuoteDuplicates = () => api.get("/admin/quote-duplicates");

// Booking option_id population diagnostic (Section 2b)
export const getBookingOptionPopulation = () =>
  api.get("/admin/diagnostics/booking-option-population");

// Booking status distribution diagnostic (Section 5b)
export const getBookingStatusDistribution = () =>
  api.get("/admin/diagnostics/booking-status-distribution");

// Stale booking option_id detail characterization (read-only)
export const getStaleBookingDetail = () =>
  api.get("/admin/diagnostics/stale-booking-detail");

// Submitted invoices diagnostic (lifecycle flip blocker, read-only)
export const getSubmittedInvoices = () =>
  api.get("/admin/diagnostics/submitted-invoices");

// Lifecycle coverage audit (canonical gating quartet, read-only)
export const getLifecycleCoverageAudit = (lifecycle = "enquiry") =>
  api.get("/admin/diagnostics/lifecycle-coverage-audit", { params: { lifecycle } });

// Guest issue ticket diagnostics
export const getGuestTicketStatusDistinct = () =>
  api.get("/admin/guest-ticket-status-distinct");
export const getGuestTicketDiagnostics = () =>
  api.get("/admin/diagnostics/guest-tickets");
export const getGuestIssueTicketSettlement = () =>
  api.get("/admin/guest-issue-ticket-lifecycle-settlement");

// Viewing diagnostics
export const getViewingStatusDistinct = () =>
  api.get("/admin/viewing-status-distinct");
export const getViewingDiagnostics = () =>
  api.get("/admin/diagnostics/viewings");
export const getViewingLifecycleSettlement = () =>
  api.get("/admin/viewing-lifecycle-settlement");

// Process split status (supervisord two-process diagnostic)
export const getProcessSplitStatus = () => api.get("/admin/diagnostics/process-split-status");

// Rejected cohort migration (bare 'rejected' -> canonical split)
export const getRejectedCohortMigrationPreview = () =>
  api.get("/admin/rejected-cohort-migration");
export const confirmRejectedCohortMigration = () =>
  api.post("/admin/rejected-cohort-migration", null, { params: { confirm: true } });

// Deposit field canonicalization backfill
export const getDepositFieldBackfillPreview = () =>
  api.get("/admin/deposit-field-backfill/preview");
export const applyDepositFieldBackfill = (dryRun = false) =>
  api.post("/admin/deposit-field-backfill/apply", { dry_run: dryRun });

// Deposit conflict triage (both_different per-row resolution)
export const getDepositConflictTriageList = () =>
  api.get("/admin/deposit-conflict-triage/list");
export const resolveDepositConflict = (bookingId, action) =>
  api.post("/admin/deposit-conflict-triage/resolve", { booking_id: bookingId, action });

// Deposit conflict population diagnostic (both_different analysis)
export const getDepositConflictPopulation = () =>
  api.get("/admin/diagnostics/deposit-conflict-population");

// SearchLifecycle stale-record repair
export const searchLifecycleStaleRepair = (dryRun = true) =>
  api.post("/admin/search-lifecycle-stale-repair", { dry_run: dryRun });

// Invoice → Booking hydration diagnostic (read-only)
export const getInvoiceBookingHydration = () =>
  api.get("/admin/diagnostics/invoice-booking-hydration");

// Hydration backfill (preview + apply)
export const getHydrationBackfillPreview = (limit) => {
  const params = limit ? { limit } : {};
  return api.get("/admin/hydration-backfill/preview", { params });
};

export const applyHydrationBackfill = (data) =>
  api.post("/admin/hydration-backfill/apply", data);

export const getDryRunEnqOnly = () =>
  api.get("/admin/hydration-backfill/dry-run-enq-only");

export const getDumpFailingRecords = () =>
  api.get("/admin/hydration-backfill/dump-failing-records");

// Booking-Lifecycle orphan detection
export const getBookingLifecycleOrphans = () =>
  api.get("/admin/diagnostics/bookings-missing-lifecycle");

export const healBookingLifecycleOrphans = () =>
  api.post("/admin/diagnostics/bookings-missing-lifecycle/heal", null, {
    params: { confirm: true },
  });

// Inbound attribution diagnostic
export const getInboundAttribution = (params) =>
  api.get("/admin/diagnostics/inbound-attribution", { params });

export const postInboundReingest = (data) =>
  api.post("/admin/diagnostics/inbound-reingest", data);

export const postClassifyEmailTrace = (data) =>
  api.post("/admin/diagnostics/classify-email", data);

// Payment classification diagnostic
export const getPaymentClassification = (params) =>
  api.get("/admin/diagnostics/payment-classification", { params });

export const postReclassifyClientRemittances = (data) =>
  api.post("/admin/diagnostics/reclassify-client-remittances", data, {
    timeout: 180000,
  });

export const postEnsureReclassifiedPayments = (data) =>
  api.post("/admin/diagnostics/ensure-reclassified-payments", data, {
    timeout: 180000,
  });

export const getAutoRegisteredDomains = () =>
  api.get("/admin/diagnostics/auto-registered-domains");

export const postUndoDomainRegistration = (data) =>
  api.post("/admin/diagnostics/undo-domain-registration", data);

// Airbnb scrape test diagnostic
export const getAirbnbScrapeTest = (url) =>
  api.get("/admin/diagnostics/airbnb-scrape-test", {
    params: { url },
    timeout: 180000,
  });

// Quote send envelope preview diagnostic
export const getQuoteSendPreview = (enquiryId, senderOverride) =>
  api.get("/admin/diagnostics/quote-send-preview", {
    params: { enquiry_id: enquiryId, ...(senderOverride ? { sender_override: senderOverride } : {}) },
  });

// Service key management
export const getServiceKeyStatus = () => api.get("/admin/keys/service-key-status");
export const mintServiceKey = (rotate = false) =>
  api.post(`/admin/keys/mint-service-key${rotate ? "?rotate=true" : ""}`);

// Bookingless invoice render diagnostic
export const getBookinglessInvoiceRender = () =>
  api.get("/admin/diagnostics/bookingless-invoice-render");

// Invoice -> Xero original link diagnostic
export const getInvoiceXeroLink = (params) =>
  api.get("/admin/diagnostics/invoice-xero-link", { params });

// Invoice -> Xero coverage diagnostic
export const getInvoiceXeroCoverage = () =>
  api.get("/admin/diagnostics/invoice-xero-coverage");

// Invoice/Booking reconciliation diagnostic
export const getInvoiceBookingRecon = (params) =>
  api.get("/admin/diagnostics/invoice-booking-recon", { params });

// Invoice amount audit diagnostic
export const getInvoiceAmountAudit = (params) =>
  api.get("/admin/diagnostics/invoice-amount-audit", { params });

// Invoice duplicates diagnostic
export const getInvoiceDuplicates = (params) =>
  api.get("/admin/diagnostics/invoice-duplicates", { params });

// Invoice GBP correction diagnostic
export const getInvoiceGbpCorrectionPreview = () =>
  api.get("/admin/diagnostics/invoice-gbp-correction-preview");

export const applyInvoiceGbpCorrection = () =>
  api.post("/admin/diagnostics/invoice-gbp-correction-apply", null, {
    params: { confirm: true },
    timeout: 120000,
  });

export const getInvoiceGbpCorrectionExplain = () =>
  api.get("/admin/diagnostics/invoice-gbp-correction-explain");

// Booking/Invoice reconciliation buckets diagnostic
export const getBookingInvoiceReconBuckets = (params) =>
  api.get("/admin/diagnostics/booking-invoice-recon-buckets", { params });

// Orphan enquiry-class diagnostic (cross-month)
export const getOrphanEnquiryClass = () =>
  api.get("/admin/diagnostics/orphan-enquiry-class");

// Xero auto-book coverage scan (Stage 0 gate)
export const getXeroAutobookCoverage = () =>
  api.get("/admin/diagnostics/xero-autobook-coverage");

// Invoice/Booking link parity diagnostic
export const getInvoiceBookingLinkParity = () =>
  api.get("/admin/diagnostics/invoice-booking-link-parity");

// Monitor summary (proxied for frontend — service token stays server-side)
export const getMonitorSummary = () => api.get("/admin/diagnostics/monitor-summary");

// Gmail draft pipeline diagnostics
export const getGmailDraftDiagnostics = () => api.get("/admin/diagnostics/gmail-drafts");

// Property coverage diagnostics
export const getPropertyCoverage = () => api.get("/admin/diagnostics/property-coverage");

// Commission snapshot backfill
export const getCommissionSnapshotBackfillPreview = () =>
  api.get("/admin/commission-snapshot-backfill/preview");
export const applyCommissionSnapshotBackfill = () =>
  api.post("/admin/commission-snapshot-backfill/apply", null, { timeout: 120000 });

// Commission invoice rebuild (pre-stamp invoices)
export const getCommissionInvoiceRebuildPreview = () =>
  api.get("/admin/commission-invoice-rebuild/preview");
export const previewCommissionInvoiceRebuild = (invoiceId) =>
  api.post(`/admin/commission-invoice-rebuild/${invoiceId}/preview`);
export const applyCommissionInvoiceRebuild = (invoiceId) =>
  api.post(`/admin/commission-invoice-rebuild/${invoiceId}/apply`, null, { timeout: 30000 });

// Extension invoice overbill diagnostics
export const getExtensionInvoiceBlastRadius = () =>
  api.get("/admin/diag/extension-invoice-blast-radius");
export const getInvoiceXeroState = (invoiceId) =>
  api.get(`/admin/diag/invoice-xero-state/${invoiceId}`);
export const getExtensionInvoiceVerify = (bookingId) =>
  api.get(`/admin/diag/extension-invoice-verify/${bookingId}`);
export const acknowledgeExtensionInvoice = (invoiceId) =>
  api.post(`/admin/diag/extension-invoice-acknowledge/${invoiceId}`);
export const unacknowledgeExtensionInvoice = (invoiceId) =>
  api.delete(`/admin/diag/extension-invoice-acknowledge/${invoiceId}`);

// Gross-rate blast radius diagnostic & correction
export const getGrossRateBlastRadius = () =>
  api.get("/admin/diag/gross-rate-blast-radius");
export const correctGrossRate = (bookingId, netRate) =>
  api.post(`/admin/diag/correct-gross-rate/${bookingId}`, { net_rate: netRate });

// Tax rate mismatch diagnostic
export const getTaxRateMismatch = () =>
  api.get("/admin/diag/tax-rate-mismatch");
export const setBookingCountry = (bookingId, country) =>
  api.post(`/admin/diag/tax-rate-mismatch/set-country/${bookingId}`, { country });
export const bulkSetBookingCountry = (items) =>
  api.post("/admin/diag/tax-rate-mismatch/bulk-set-country", { items });

// Invoice currency mismatch diagnostic & correction
export const getInvoiceCurrencyMismatch = () =>
  api.get("/admin/diagnostics/invoice-currency-mismatch");
export const correctInvoiceCurrency = (invoiceId) =>
  api.post(`/admin/diagnostics/invoice-currency-mismatch-correct/${invoiceId}`);

// Commission rate mismatch diagnostic
export const getCommissionRateMismatch = () =>
  api.get("/admin/diagnostics/commission-rate-mismatch");
export const clearCommissionSnapshot = (bookingId) =>
  api.post(`/admin/diagnostics/commission-rate-mismatch-clear/${bookingId}`);

// Orphaned option classifications diagnostics
export const getOrphanedOptionClassifications = () =>
  api.get("/admin/diagnostics/orphaned-option-classifications");
export const createPendingOptionFromOrphan = (communicationId) =>
  api.post(`/admin/diagnostics/orphaned-option-classifications/${communicationId}/create-pending`);
export const recoverAllOrphanedOptions = () =>
  api.post("/admin/diagnostics/orphaned-option-classifications/recover-all");

// Website Enquiry Webhook diagnostics
export const getWebsiteEnquiryLog = (limit = 20) =>
  api.get("/website-enquiry-webhook/log", { params: { limit } });
export const sendWebsiteEnquiryTest = () =>
  api.post("/website-enquiry-webhook/test", null, { timeout: 30000 });
export const remapWebsiteEnquiry = (enquiryId) =>
  api.post(`/website-enquiry-webhook/remap/${encodeURIComponent(enquiryId)}`);

// Booking totals/margin backfill diagnostic
export const getBookingTotalsBackfillPreview = () =>
  api.get("/admin/diagnostics/booking-totals-backfill-preview");
export const applyBookingTotalsBackfill = () =>
  api.post("/admin/diagnostics/booking-totals-backfill-apply", null, {
    params: { confirm: true },
    timeout: 120000,
  });

// Invoice currency repair diagnostic (Commit 3)
export const getInvoiceCurrencyRepairPreview = () =>
  api.get("/admin/diagnostics/invoice-currency-repair-preview");
export const applyInvoiceCurrencyRepair = () =>
  api.post("/admin/diagnostics/invoice-currency-repair-apply", null, {
    params: { confirm: true },
    timeout: 120000,
  });

// Feed endpoints (read-only, consumed by GE for suppression)
export const getSupplierFeedSummary = () => api.get("/suppliers/summary");
export const getOpenEnquiryFeedSummary = () => api.get("/enquiries/summary");

// Email Pipeline Health
export const getEmailPipelineDiagnostics = () => api.get("/admin/diagnostics/email-pipeline");

// Supplier-origin enquiries diagnostic
export const getSupplierOriginEnquiries = () => api.get("/admin/diagnostics/supplier-origin-enquiries");

// Admin - House FX rates
export const getFxRates = (compareLive = true) =>
  api.get("/admin/fx-rates", { params: { compare_live: compareLive } });
export const setFxRate = (data) => api.put("/admin/fx-rates", data);
export const deleteFxRate = (pair) => api.delete(`/admin/fx-rates/${encodeURIComponent(pair)}`);
export const getFxBookingCoverage = () => api.get("/admin/fx-rates/booking-coverage");

// Admin - Team & client access (per-user roles and client assignment)
export const getTeamUsers = () => api.get("/admin/users");
export const getAssignableClients = () => api.get("/admin/users/assignable-clients");
export const upsertTeamUser = (data) => api.post("/admin/users", data);
export const patchTeamUser = (email, data) =>
  api.patch(`/admin/users/${encodeURIComponent(email)}`, data);
export const deactivateTeamUser = (email) =>
  api.delete(`/admin/users/${encodeURIComponent(email)}`);

// Scoped portfolio view (own clients)
export const getMyPortfolio = () => api.get("/my-portfolio/summary");
// Everything on My Clients as one .xlsx (same client scope as the summary).
export const downloadMyPortfolioXlsx = () =>
  downloadAuthenticatedFile("/my-portfolio/export.xlsx", "My clients.xlsx");

// Key account managers: revenue, Slack channel copies, record ownership
export const getMyRevenue = (params = {}) => api.get("/my-portfolio/revenue", { params });
export const getKamRevenue = (params = {}) => api.get("/admin/account-managers/revenue", { params });
export const getMyScheme = (params = {}) => api.get("/my-portfolio/scheme", { params });
export const getMyStatementPdf = (params = {}) =>
  api.get("/my-portfolio/scheme/statement.pdf", { params, responseType: "blob", timeout: 90000 });
export const getKamSchemes = (params = {}) => api.get("/admin/account-managers/schemes", { params });
export const putKamScheme = (email, year, body) =>
  api.put(`/admin/account-managers/${encodeURIComponent(email)}/schemes/${year}`, body);
export const patchSlackMirror = (email, body) =>
  api.patch(`/admin/users/${encodeURIComponent(email)}/slack-mirror`, body);
export const testSlackMirror = (email) =>
  api.post(`/admin/users/${encodeURIComponent(email)}/slack-mirror/test`);
export const runKamAttribution = (dryRun = true) =>
  api.post("/admin/account-managers/attribution/run", null, { params: { dry_run: dryRun } });
export const assignRecordOwner = (recordType, recordId, email) =>
  api.post("/admin/account-managers/assign", { record_type: recordType, record_id: recordId, email });
export const addTicketReport = (ticketId, data) => api.post(`/tickets/${ticketId}/report`, data);
export const getOpenTicketForBooking = (bookingId) => api.get(`/tickets/open-for-booking/${bookingId}`);
